import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield } from 'lucide-react';

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, formatError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        const user = await login(email, password);
        navigate(user.role === 'admin' ? '/admin' : '/dashboard');
      } else {
        await register(name, email, password);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Shield className="w-10 h-10 text-navy" />
            <span className="font-heading font-bold text-navy text-2xl">SAFE FLORIDA</span>
          </Link>
          <h1 className="text-2xl font-heading font-bold text-navy">
            {isLogin ? 'Welcome Back' : 'Join Safe Florida'}
          </h1>
          <p className="text-sm text-gray-600 font-body mt-1">
            {isLogin ? 'Log in to your member portal' : 'Create your member account'}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm font-body rounded-md" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="font-body text-sm text-gray-700">Full Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" required className="mt-1 font-body" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="font-body text-sm text-gray-700">Email Address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="mt-1 font-body" data-testid="auth-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="font-body text-sm text-gray-700">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" required minLength={6} className="mt-1 font-body" data-testid="auth-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-primary-safe font-body font-bold" data-testid="auth-submit-button">
              {loading ? 'Please wait...' : isLogin ? 'Log In' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-sm text-navy hover:text-gold font-body transition-colors" data-testid="auth-toggle-button">
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
